import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import { useEffect, useState } from "react";
import { defineMessage, useIntl } from "react-intl";
import type { PriceListItem } from "../InvoiceCard";
import {
  useInvoicingPriceListV2LazyQuery,
  useInvoicingPriceListV2MetaQuery,
} from "./__generated__/useFetchPriceListV2.gql";

const ERROR_MESSAGE = defineMessage({
  id: "invoicing.getPriceList.error.msg",
  defaultMessage: "Failed to fetch price list",
});

gql`
  query invoicingPriceListV2Meta($input: PriceListMetaInput!) {
    priceListV2 {
      priceListMeta(input: $input) {
        id
      }
    }
  }
`;

gql`
  query invoicingPriceListV2($input: PriceListItemsInput!, $options: PriceListItemsOptions) {
    priceListV2 {
      priceListItems(input: $input, options: $options) {
        edges {
          id
          name
          externalReference {
            origin
            foreignId
          }
          prices {
            id
            value
            tariff {
              id
              name
            }
            revenueDepartment {
              name
            }
          }
        }
      }
    }
  }
`;

type ReturnType = Omit<PriceListItem, "cost" | "priceListItemType">;

export default function useFetchPriceListV2() {
  const { facilityId } = useUser();
  const [priceListId, setPriceListId] = useState<string>(null);
  const [priceListItems, setPriceListItems] = useState<ReturnType[]>([]);
  const { formatMessage } = useIntl();

  const {
    data: metaData,
    loading: metaLoading,
    error: metaError,
  } = useInvoicingPriceListV2MetaQuery({
    variables: { input: { facilityId } },
    fetchPolicy: "no-cache",
    onError() {
      toast.error(formatMessage(ERROR_MESSAGE), {});
    },
  });

  const [fetchPrices, { data, loading, error }] =
    useInvoicingPriceListV2LazyQuery({
      // Quick win: fetch all items instead of implementing name search
      // https://linear.app/elephant-healthcare/issue/ENG-85/cant-add-manual-items-to-invoice-not-on-1st-page-of-price-list
      variables: {
        input: { priceListId },
        options: { pagination: { pageSize: 100000 } },
      },
      fetchPolicy: "cache-and-network",
      onError() {
        toast.error(formatMessage(ERROR_MESSAGE), {});
      },
    });

  useEffect(() => {
    if (metaLoading || metaError || !metaData?.priceListV2.priceListMeta?.id) {
      return;
    }
    setPriceListId(metaData.priceListV2.priceListMeta.id);
  }, [metaData, metaLoading, metaError]);

  useEffect(() => {
    if (priceListId) {
      fetchPrices();
    }
  }, [priceListId, fetchPrices]);

  useEffect(() => {
    if (loading || error || !data) {
      return;
    }
    const edges = data.priceListV2.priceListItems.edges.map<ReturnType>(
      (e) => ({
        ...e,
        payers: e.prices.map((p) => ({
          tariffId: p.tariff.id,
          name: p.tariff.name,
          amountInPence: p.value,
          revenueDepartment: p.revenueDepartment.name,
        })),
      }),
    );
    setPriceListItems(edges);
  }, [data, loading, error]);

  return { loading, error, priceList: { priceListItems } };
}
